import { useState, useCallback, useEffect } from 'react'
import type { AppView, Adventure, StudentProgress, AdventureProgress } from './types'

// Views
import HomeView from './components/Layout/HomeView'
import AdventureView from './components/Adventure/AdventureView'
import ProgressView from './components/Layout/ProgressView'

// Adventure data
import { getAdventureById } from './data/adventures'

// Storage keys
const STORAGE_KEY = 'flow-education-v2-progress'

function App() {
  const [currentView, setCurrentView] = useState<AppView>('home')
  const [activeAdventure, setActiveAdventure] = useState<Adventure | null>(null)
  const [progress, setProgress] = useState<StudentProgress>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) {
        return JSON.parse(saved)
      }
    } catch (e) {
      console.error('Failed to load progress:', e)
    }
    return {
      currentAge: null,
      completedAdventures: [],
      unlockedBadges: [],
      totalTimeSpent: 0,
      lastAccessed: null,
      currentStreak: 0,
    }
  })

  // Persist progress
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(progress))
    } catch (e) {
      console.error('Failed to save progress:', e)
    }
  }, [progress])

  const handleStartAdventure = useCallback((adventure: Adventure) => {
    // Get full adventure data with segments
    const fullAdventure = getAdventureById(adventure.id)
    if (fullAdventure) {
      setActiveAdventure(fullAdventure)
      setCurrentView('adventure')
      setProgress(prev => ({
        ...prev,
        lastAccessed: new Date().toISOString(),
      }))
    }
  }, [])

  const handleCompleteAdventure = useCallback((adventureProgress: AdventureProgress) => {
    setProgress(prev => ({
      ...prev,
      completedAdventures: [...prev.completedAdventures, adventureProgress],
      totalTimeSpent: prev.totalTimeSpent + adventureProgress.timeSpent,
    }))
    setActiveAdventure(null)
    setCurrentView('home')
  }, [])

  const handleExitAdventure = useCallback(() => {
    setActiveAdventure(null)
    setCurrentView('home')
  }, [])

  const handleSetAge = useCallback((age: '3' | '4' | '5') => {
    setProgress(prev => ({ ...prev, currentAge: age }))
  }, [])

  return (
    <div className="h-screen w-screen overflow-hidden bg-gradient-to-b from-orange-50 to-white">
      {currentView === 'home' && (
        <HomeView
          progress={progress}
          onStartAdventure={handleStartAdventure}
          onViewProgress={() => setCurrentView('progress')}
          onSetAge={handleSetAge}
        />
      )}

      {currentView === 'adventure' && activeAdventure && (
        <AdventureView
          adventure={activeAdventure}
          onComplete={handleCompleteAdventure}
          onExit={handleExitAdventure}
        />
      )}

      {currentView === 'progress' && (
        <ProgressView
          progress={progress}
          onBack={() => setCurrentView('home')}
        />
      )}
    </div>
  )
}

export default App
